/**
 * Create-a-passkey prompt.
 *
 * A one-time offer, shown right after a fresh OAuth or OTP login to a user who has
 * no passkey enrolled, to add a device passkey so the next sign-in is a fingerprint,
 * face, or screen-lock tap with no password to remember. Passkey creation moved off
 * the login screen to keep that screen a single "continue" surface, so this prompt
 * plus the Settings security section are where a passkey gets created.
 *
 * It is encouraged but dismissible. Creating runs the same enroll flow the Settings
 * security section uses (registerDevice through the device service), so a created
 * passkey appears in the enrolled list and can later approve transactions. "Don't
 * show this again" persists a flag to localStorage so the offer never nags; a plain
 * close leaves that flag unset so a later fresh login on this device could offer it
 * again until a passkey exists (at which point the no-passkey gate stops it).
 *
 * It reuses the shared sheet shell for its modal chrome, traps focus, closes on
 * Escape and on a backdrop click, restores focus to the opener on close, and marks
 * itself aria-modal so the rest of the screen is inert.
 */

import { useRef, useState } from 'react';

import { enrollDevice } from '@/services/devices';
import { persistCreatePasskeyDismissed } from '@/lib/createPasskeyPref';
import { getSlice } from '@/store/store';
import { useT } from '@/i18n';
import { useBottomSheet } from '@/components/useBottomSheet';
import { useDialogTrap } from '@/components/useDialogTrap';

/** Props for the create-passkey prompt. */
interface CreatePasskeyPromptProps {
  /**
   * Close the dialog. Called after a successful creation, on Escape, and on a
   * backdrop click. The parent decides what closing means, for example clearing
   * the one-time fresh-login flag so the prompt does not reappear this session.
   */
  onClose: () => void;
}

/**
 * Render the prompt and drive the create flow. Holds only a busy flag and an
 * inline error; the session is already established, so the enroll call runs
 * authenticated through the device service.
 */
export default function CreatePasskeyPrompt({ onClose }: CreatePasskeyPromptProps) {
  const t = useT();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);

  // Drag-to-dismiss + body scroll lock, matching p2p's bottom sheet.
  const { dragProps } = useBottomSheet(panelRef, onClose);

  // Focus in on open, Tab trapped inside, Escape closes, focus restored on close.
  useDialogTrap(panelRef, onClose);

  // Run the enroll ceremony, the same path the Settings security section uses. The
  // gateway names the method; nothing here chooses a label. On success the passkey is enrolled and the prompt
  // closes; a cancelled ceremony or a gateway error surfaces a calm inline message
  // and leaves the prompt open so the user can retry or dismiss.
  async function create() {
    if (busy) return;
    setError(null);
    setBusy(true);
    const ok = await enrollDevice();
    setBusy(false);
    if (ok) {
      onClose();
      return;
    }
    // Prefer the reason the service recorded over a generic failure line. It is read
    // from the store rather than the render-time snapshot because the snapshot in
    // this closure predates the call that just set it. Settings shows the same
    // string; without this the one enrolment path most likely to hit "this device
    // already has a passkey" would be the one that never says so.
    setError(getSlice('devices').error ?? t('passkey.create.error'));
  }

  // Persist the never-show-again choice, then close.
  function dismissForever() {
    persistCreatePasskeyDismissed();
    onClose();
  }

  return (
    <div className="sheet-backdrop">
      {/* A native button fills the backdrop so a click outside the panel dismisses
          it and stays keyboard-reachable. A plain dismiss does not persist the
          never-show-again flag, so the offer can return on a later fresh login
          until a passkey exists. */}
      <button
        type="button"
        className="sheet-backdrop__dismiss"
        aria-label={t('common.close')}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="sheet create-passkey"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-passkey-title"
      >
        <span className="sheet__grab" aria-hidden="true" {...dragProps} />
        <h2 className="sheet__title" {...dragProps} id="create-passkey-title">
          {t('passkey.create.title')}
        </h2>

        <div className="create-passkey__body">
          <p className="create-passkey__lead text-muted">{t('passkey.create.body')}</p>

          {error && (
            <p className="create-passkey__error" role="alert">
              {error}
            </p>
          )}

          <div className="create-passkey__actions">
            <button type="button" disabled={busy} onClick={create}>
              {busy ? t('passkey.create.creating') : t('passkey.create.cta')}
            </button>
            <button type="button" data-variant="ghost" disabled={busy} onClick={dismissForever}>
              {t('passkey.create.dismiss')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
